import type { Polygon } from "geojson";
import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

import type { Zone, ZoneSource } from "@mountayaapp/types/api/api";

import type { ItinerarySliceState } from "./itinerary";

/**
 * zoneEditModeSchema is the closed set of zone authoring modes — the geometric
 * primitive a zone is drawn as. It mirrors the wire ZoneMode enum and the map
 * leaf's structural twin (`@mountayaapp/map` zoneModeSchema): every mode draws a
 * filled Polygon area — `freehand` traces its area by a single drag while the rest
 * place it parametrically or vertex-by-vertex. `select` is NOT a member — it is
 * TerraDraw's edit mode the engine arms alongside the active draw mode, never a
 * zone's own mode. Zod-first because the value is shared with the
 * zone editBegin tool input and validated when an agent (or the UI) opens an
 * edit. The mode is immutable for the life of an edit — a draw never swaps modes
 * mid-session.
 */
export const zoneEditModeSchema = z.enum(["circle", "polygon", "sector", "sensor", "freehand"]);

/**
 * DEFAULT_ZONE_MODE is the geometric primitive a fresh zone create defaults to
 * before the user picks a shape — `circle`, the quickest area to place and adjust.
 * It seeds the create form's shape picker (so the Create-and-draw action is enabled
 * from the start) and the editing hook's engine arm when no session mode is set yet,
 * kept here next to {@link zoneEditModeSchema} so the create-default is defined once.
 */
export const DEFAULT_ZONE_MODE: ZoneEditMode = "circle";

/**
 * ZoneEditMode is one zone authoring mode, derived from zoneEditModeSchema. The
 * web zone engine maps it onto a single TerraDraw draw mode for the session.
 */
export type ZoneEditMode = z.infer<typeof zoneEditModeSchema>;

/**
 * zoneCreateMethodSchema is how the user is defining a new zone: `draw` places
 * the shape by hand with the draw engine, `reachability` computes it from an
 * origin, an activity and a travel budget. It is the create flow's single branch
 * key — an EDIT derives the same distinction from whether the saved zone carries
 * a `source`, so the two never need a parallel notion of "what kind of zone is
 * this". Zod-first because it crosses the bus boundary on the zone create-begin
 * tool input.
 */
export const zoneCreateMethodSchema = z.enum(["draw", "reachability"]);

/**
 * ZoneCreateMethod is one zone authoring method, derived from
 * zoneCreateMethodSchema.
 */
export type ZoneCreateMethod = z.infer<typeof zoneCreateMethodSchema>;

/**
 * DEFAULT_ZONE_CREATE_METHOD is the method a fresh create opens on when nothing
 * seeded one — drawing by hand, the method that needs no upstream call and works
 * for every activity.
 */
export const DEFAULT_ZONE_CREATE_METHOD: ZoneCreateMethod = "draw";

/**
 * ZoneCreateSeed is the starting state of a zone create: the authoring method to
 * open on, the origin to seed a reachability create with (null for a draw, or for a
 * reachability create the user will place the origin for themselves), and whether
 * the create takes its context from the itinerary currently open. Every field is
 * required so a caller states the whole starting state rather than inheriting half
 * of a previous create's.
 *
 * `inheritItinerary` is the difference between the two ways a create starts. Begun
 * from the map INSIDE an open itinerary, the owning organization, the collections
 * and the activity are already settled — asking for them again offers three chances
 * to file the zone somewhere the itinerary is not. Begun from the sidenav, there is
 * no itinerary in play and all three are the user's to choose.
 */
export interface ZoneCreateSeed {
  inheritItinerary: boolean;
  method: ZoneCreateMethod;
  origin: [number, number] | null;
}

/**
 * zoneLevelSchema is the closed set of zone severity levels. It mirrors the wire
 * ZoneLevel enum and the map leaf's twin, and drives the color the live draft +
 * the persisted tiles paint (see ZONE_LEVEL_COLORS). The value is `resolved`
 * (NOT `success`). Zod-first because the level crosses the bus boundary from the
 * panel's severity picker (and an agent call) into the create/update tool input.
 */
export const zoneLevelSchema = z.enum(["info", "resolved", "warning", "danger"]);

/**
 * ZoneEditLevel is one zone severity level, derived from zoneLevelSchema.
 */
export type ZoneEditLevel = z.infer<typeof zoneLevelSchema>;

/**
 * DEFAULT_ZONE_LEVEL is the severity a fresh zone create starts at before the
 * user picks one — the least-alarming `info`. The zone panel seeds its severity
 * picker from it and the editing hook seeds the engine's first-frame color from
 * it, so the create-default lives here next to {@link zoneLevelSchema} rather
 * than duplicated at each consumer.
 */
export const DEFAULT_ZONE_LEVEL: ZoneEditLevel = "info";

/**
 * zoneEditBeginSchema is the input that starts a zone geometry edit: the zone id
 * the session is keyed on (a freshly-generated client id while creating, the
 * existing zone id while editing), the authoring mode (immutable for the
 * session), the severity level the draft renders its color from, whether the
 * session is a fresh create (no seeded shape until the first draw gesture) or an
 * edit of a saved zone, and the optional seed geometry an edit opens with (the
 * Polygon every zone mode draws; omitted for a create). Zod-first because it is
 * shared with the zone editBegin tool input and validated when an agent (or the
 * panel) opens an edit; the draft geometry and its history are not part of this
 * input — they accrue in the slice as the edit proceeds. The seed geometry is
 * widened to a structural Polygon and NOT deeply Zod-validated, the same trust
 * boundary the segment session's draft geometry takes (GeoJSON is structurally
 * large and trusted in-process).
 */
export const zoneEditBeginSchema = z.object({
  intent: z.enum(["create", "edit"]),
  level: zoneLevelSchema,
  mode: zoneEditModeSchema,
  // The owning organization a CREATE session is opened for — the panel's Step 1
  // choice, or an agent proposal's org — carried onto the session so the locked
  // create surface, the co-pilot entry, and the commit all read one source (the
  // zone analogue of the itinerary's org living on its persisted record). Absent
  // or null for an edit: a saved zone's org is fixed on the record.
  organizationId: z.string().nullish(),
  seedGeometry: z.custom<Polygon>().optional(),
  // The generative provenance a computed proposal opens with (an isochrones
  // reachability query), carried onto the session so the human save can persist
  // the zone WITH its source rather than as a hand-drawn shape. Absent (or null)
  // for a hand-drawn proposal/edit; widened structurally (the wire ZoneSource) and
  // not deeply Zod-validated here, the same trust boundary the seed geometry takes.
  source: z.custom<ZoneSource>().nullish(),
  zoneId: z.string(),
});

/**
 * ZoneEditBegin is the zone + mode + level a zone edit starts with, derived from
 * zoneEditBeginSchema.
 */
export type ZoneEditBegin = z.infer<typeof zoneEditBeginSchema>;

/**
 * ZoneEditSession is the active zone geometry edit: the zone being authored, the
 * authoring mode (immutable for the session), the severity level the draft and
 * its swatches render their color from, whether the session is a fresh `create`
 * or an `edit` of a saved zone, the current draft geometry (null when the edit
 * starts empty — a from-scratch create before the first draw gesture lands), and
 * the undo/redo history as past/future stacks of prior draft snapshots. It is
 * the zone-family analogue of {@link SegmentEditSession}: the map adapter
 * re-seeds the TerraDraw zone engine from `draft` whenever it changes (the seam
 * the panel and the agent both drive), and undo/redo expose the geometry to
 * re-apply by writing it back to `draft`. A pushed draft change moves the prior
 * draft (including the empty null seed, so undo can return to the starting
 * state) onto `past` and clears `future`; undo moves the current draft to
 * `future` and restores the top of `past`; redo does the reverse.
 *
 * The draft is a GeoJSON Polygon (the single source the persisted Feature is
 * built from on create) and is not Zod-validated here, the same trust
 * boundary the segment session's draft takes — GeoJSON is structurally large and
 * trusted in-process. It is deliberately kept a distinct type from
 * segment/waypoint sessions: a zone session carries no routing state, no live
 * routed segment, and no POI snapshot — only the drawn shape + its mode + level —
 * so forcing it into a shared shape would replace clear per-family fields with a
 * union readers must narrow. The behavior that IS shared lives one layer down on
 * the map engines (see `EditEngine`) and one layer up in the tool-bus teardown
 * (`teardownActiveEditSession`).
 */
export interface ZoneEditSession {
  draft: Polygon | null;
  future: (Polygon | null)[];
  intent: "create" | "edit";
  level: ZoneEditLevel;
  mode: ZoneEditMode;
  /**
   * organizationId is the owning organization a CREATE session was opened for (the
   * panel's Step 1 choice, or an agent proposal's org), carried here so the locked
   * create surface, the co-pilot entry (ZoneContent), and the co-pilot's resource
   * resolution (CopilotPanel) read one source while nothing is persisted yet — the
   * zone analogue of the itinerary's org living on activeItinerary. Null for an edit
   * of a saved zone, whose org is fixed on the record.
   */
  organizationId: string | null;
  past: (Polygon | null)[];
  /**
   * savedGeometry is the last PERSISTED shape the live draft is compared against
   * to decide whether the edit has unsaved changes (the dirty signal the Save
   * button reads through {@link selectZoneEditDirty}). An edit of a saved zone
   * opens with the baseline = its canonical geometry (so it opens clean — Save is
   * disabled until a reshape diverges); a fresh create opens with null (no shape
   * persisted yet, so a drawn shape is itself the dirty signal). A successful
   * save advances the baseline to the just-persisted geometry through
   * {@link ZoneEditSliceActions.setZoneSavedGeometry}, so the session stays armed
   * but the Save button disables again until the next change. It is the zone
   * analogue of the segment session's persisted-vs-draft comparison, kept on the
   * session so the selector reads one source of truth.
   */
  savedGeometry: Polygon | null;
  /**
   * source is the generative provenance the session carries while a COMPUTED
   * proposal is open (an isochrones reachability query the agent or the create
   * wizard produced): the human save persists the zone with this `source` so the
   * backend recomputes its authoritative polygon, rather than persisting the draft
   * as a hand-drawn shape. It is null for a hand-drawn create/edit. Any INTERACTIVE
   * geometry mutation of the draft (pushZoneDraft — a draw or a vertex drag) clears
   * it to null: reshaping the computed shape by hand is a one-way, in-session detach
   * (the same rule the backend applies to a segment when its routed geometry is
   * manually reshaped), so a subsequent save carries the drawn geojson, not the
   * stale query. A programmatic re-seed (applyZoneDraft, an undo/redo restore) does
   * NOT clear it — only a human edit does.
   */
  source: ZoneSource | null;
  zoneId: string;
}

/**
 * ZonePreviewBand is one reachability band the create wizard previews on the map
 * before persisting: the isochrone polygon the data API computed for a single
 * range value plus that value (seconds for a time range, meters for a distance
 * range, kept for a legend/label). The wizard computes the whole set client-side
 * and the preview-sync hook paints their outlines; the persisted zones are created
 * one-per-band with a `source`, and the backend computes each authoritative
 * polygon, so these preview shapes are never themselves persisted.
 */
export interface ZonePreviewBand {
  geometry: Polygon;
  value: number;
}

/**
 * zonePreviewQueryKey is the identity of the upstream call a preview came from:
 * everything that changes the SHAPE (the origin, the activity, the measure, the
 * band values, the pace) and nothing that only changes how it is PAINTED. The
 * preview tool compares the incoming key with the one on the slot and skips the
 * request when they match, so recoloring a band by severity — or re-picking the
 * value it already holds — repaints without spending an upstream call, which is
 * metered per request. Built here rather than at the call site so the writer and
 * the comparer can never disagree about what counts as a different shape.
 */
export const zonePreviewQueryKey = (input: {
  activity: string;
  location: [number, number];
  ranges: number[];
  rangeType: string;
  speed?: number;
}): string =>
  JSON.stringify([
    input.activity,
    input.location,
    input.rangeType,
    input.ranges,
    input.speed ?? null,
  ]);

/**
 * ZonePreview is the transient reachability overlay the zone panel shows while a
 * computed zone is being configured: the computed bands, the `origin` they are
 * measured from, the `activity` they were computed for, the severity `level` they
 * paint in (the hook resolves the color through ZONE_LEVEL_COLORS), and the
 * `queryKey` identifying the upstream call they came from. Null when no preview is up.
 *
 * `activity` is carried for the PAINT, not for the query — the query's own copy is
 * already folded into `queryKey`. The origin marker draws the activity's silhouette,
 * so a band set says what it was measured for without a legend: the same 30 minutes
 * on foot and on a road bike are different areas, and the marker is where that reads.
 *
 * Held distinct from the itinerary preview inputs so the two never share a sink —
 * the one preview-sync hook reads both and paints exactly one overlay, so there is
 * no last-writer-wins contention between a suggested route and a zone band set.
 */
export interface ZonePreview {
  activity: string;
  bands: ZonePreviewBand[];
  level: ZoneEditLevel;
  origin: [number, number] | null;
  queryKey: string;
}

/**
 * ZoneEditSliceState is the active zone edit session and the zone the panel is
 * bound to.
 */
export interface ZoneEditSliceState {
  /**
   * zoneCreateMethod is how the zone being created is defined — by hand with the
   * draw engine, or computed from a reachability query. It is the create flow's
   * branch key, seeded when the create surface opens (the map context menu's
   * "Reachability from here…" seeds `reachability`, the sidenav entry `draw`) and
   * changed by the panel's own method picker. Null when no create is being set
   * up. An EDIT does not read it: a saved zone's method is derived from whether
   * it carries a `source`, so a stale value can never mislabel an existing zone.
   */
  zoneCreateMethod: ZoneCreateMethod | null;
  /**
   * zoneCreateInheritsItinerary is true while the create in progress takes its
   * organization, collections and activity from the itinerary open in the editor
   * rather than asking for them. Seeded by the entry point (the map's context menu
   * sets it, the sidenav's zone button does not) and false whenever no create is
   * being set up. The panel reads it to drop those three controls and state what was
   * inherited instead. An EDIT never reads it: a saved zone carries its own org.
   */
  zoneCreateInheritsItinerary: boolean;
  /**
   * zoneCreateOrigin is the [lng, lat] point a reachability create measures from,
   * or null before one is placed. Seeded from the right-clicked coordinate when
   * the create is opened from the map, and rewritten by each map pick. Set only
   * through the tool bus.
   */
  zoneCreateOrigin: [number, number] | null;
  /**
   * zoneOriginPicking is true while the map is armed to capture the next click as
   * the reachability origin. It is the zone-family counterpart of the suggest
   * session's `picking` slot and the segment split's pick slot: the picking hook
   * arms off it, and the one-shot setter clears it, so the whole cursor + tooltip
   * + engine lifecycle tears down without an explicit stop call.
   */
  zoneOriginPicking: boolean;
  /**
   * zonePreview is the reachability overlay the zone panel paints while a
   * computed zone is being configured, or null when none is up. It is set only
   * through the tool bus (the reachability-preview tool computes the bands from
   * the data API and writes them here) and read by the preview-sync hook. Kept
   * separate from the itinerary preview state so the two preview sources never
   * contend for the single map overlay sink.
   */
  zonePreview: ZonePreview | null;
  /**
   * activeZone is the zone the zone panel is open for, or null when no zone is
   * being viewed or authored. It is the zone-family analogue of activeItinerary:
   * the panel reads it to render a saved zone's metadata for an edit, and a
   * create flow clears it (the in-flight shape lives on zoneEditSession until
   * the create persists). Set only through the tool bus. Server state otherwise
   * stays in the query cache; this slot holds the one zone the panel is bound
   * to, the same decoupling activeItinerary makes for the itinerary editor.
   */
  activeZone: Zone | null;
  /**
   * zoneEditSession is the active zone geometry edit (null while no zone is
   * being authored). Mutually exclusive with segmentEditSession AND
   * waypointEditSession by tool-layer policy (the editBegin handlers reject
   * opening a second session while any is already armed) — the discriminated
   * `selectActiveEditEngine` selector folds all three into the canonical "an
   * engine is armed" predicate so one authoring surface owns the cursor/engine
   * at a time. Kept a distinct type from the other two sessions on purpose — see
   * the note on ZoneEditSession for why it models a different concern.
   */
  zoneEditSession: ZoneEditSession | null;
}

/**
 * ZoneEditSliceActions are the zone authoring session lifecycle plus the draft
 * history and metadata setters, reached only through the tool bus.
 */
export interface ZoneEditSliceActions {
  /**
   * beginZoneEdit opens a zone geometry edit session — the single record the map
   * seam and the zone editing hook both read to arm the TerraDraw zone engine and
   * surface the in-flight draft. The session seeds its draft from the optional
   * seed geometry (an edit of a saved zone opens with its shape; a create opens
   * empty until the first draw gesture lands) and starts with empty undo/redo
   * stacks. A second open replaces the previous session — mutual exclusion across
   * the segment/waypoint/zone families is enforced one level up at the tool layer,
   * so the store action itself is trustful. Clears any prior selection so the
   * itinerary-focused emphasis paint does not linger over a zone edit.
   */
  beginZoneEdit: (begin: ZoneEditBegin) => void;
  /**
   * cancelZoneEdit closes the active zone edit session without persisting (the
   * slot becomes null). The tool layer disposes the map engine and restores the
   * zone's tile copy; the store action's only job is to clear the session so a
   * re-open reads a clean state. A no-op when no zone session is active, so a
   * defensive call from a navigate-away guard is safe.
   */
  cancelZoneEdit: () => void;
  /**
   * commitZoneEdit closes the active zone edit session after the tool layer has
   * persisted the draft (POST on create, PUT-with-geojson on a reshape). The
   * store action's only job is to clear the session so the engine can tear down;
   * a no-op when no zone session is active.
   */
  commitZoneEdit: () => void;
  /**
   * pushZoneDraft records the previous draft on `past`, clears `future`, and
   * writes the new draft. The interactive engine emissions (a draw, a vertex
   * drag, reported through onZoneDraftChange) funnel through this so every
   * meaningful change records a single history entry; the empty null seed is
   * pushed too, so an undo can return to the starting state. A no-op when no
   * zone session is active. Mirrors pushSegmentDraft.
   */
  pushZoneDraft: (geometry: Polygon) => void;
  /**
   * redoZoneDraft moves the head of `future` back onto the current draft,
   * pushing the prior draft onto `past`. A no-op when no zone session is active
   * or nothing is ahead. Mirrors redoSegmentDraft.
   */
  redoZoneDraft: () => void;
  /**
   * rekeyZoneEditSession re-keys the active zone edit onto a real persisted id and
   * flips its intent to `edit`, WITHOUT touching the draft, the undo/redo history,
   * the mode, or the level. It is the create→edit promotion the first save of a
   * new zone runs: the create persisted under a client-minted id, so the session
   * swaps to the server's id and becomes a reshape session, while the drawn shape
   * the user is still looking at stays exactly as-is (no engine churn, no camera
   * move — the map re-keys its live engine in place). A no-op when no zone session
   * is active. Pair it with {@link ZoneEditSliceActions.setZoneSavedGeometry} to
   * advance the dirty baseline to the just-persisted shape so Save disables until
   * the next change.
   */
  rekeyZoneEditSession: (zoneId: string) => void;
  /**
   * setActiveZone sets (or, with null, clears) the zone the panel is bound to.
   * The whole-slot setter the open/close tools drive; an edit opens it with the
   * saved zone, a create clears it. Mirrors setActiveItinerary.
   */
  setActiveZone: (zone: Zone | null) => void;
  /**
   * setZonePreview sets (or, with null, clears) the reachability overlay the zone
   * panel paints. The reachability-preview tool computes the bands from the data
   * API and writes them here; the preview-sync hook reads the slot and paints the
   * band outlines plus the origin pin. A whole-slot setter — the panel recomputes
   * the full preview on each field change rather than patching one band.
   */
  setZonePreview: (preview: ZonePreview | null) => void;
  /**
   * resetZoneCreate starts a fresh zone create at the given seed, clearing any
   * origin pick left armed. It is the single action every entry into the create
   * surface runs, so a create can never inherit half of a previous one's state —
   * the sidenav entry seeds `draw` with no origin, the map context menu seeds
   * `reachability` with the right-clicked coordinate.
   */
  resetZoneCreate: (seed: ZoneCreateSeed) => void;
  /**
   * setZoneCreateMethod switches the create surface between drawing the zone by
   * hand and computing it from a reachability query. The origin is deliberately
   * NOT cleared: switching to draw and back keeps the point the user already
   * placed.
   */
  setZoneCreateMethod: (method: ZoneCreateMethod) => void;
  /**
   * setZoneCreateOrigin writes the [lng, lat] a reachability create measures from
   * and clears the picking flag, so a map pick is one-shot: the click that places
   * the point also disarms the pick, and the whole cursor + tooltip + engine
   * lifecycle tears down off that flag.
   */
  setZoneCreateOrigin: (origin: [number, number] | null) => void;
  /**
   * setZoneOriginPicking arms (or disarms) the map to capture the next click as
   * the reachability origin.
   */
  setZoneOriginPicking: (picking: boolean) => void;
  /**
   * setZoneEditDraft writes the draft geometry on the active zone session
   * WITHOUT recording history — the direct seam an undo/redo replay or a
   * programmatic re-seed uses (the history-recording path is pushZoneDraft). A
   * no-op when no zone session is active. Mirrors setSegmentEditDraft.
   */
  setZoneEditDraft: (geometry: Polygon | null) => void;
  /**
   * setZoneEditLevel changes the severity level on the active zone session so
   * the draft re-colors live (the tool layer additionally drives
   * setZoneDraftStyle on the map). The level is metadata, not geometry, so this
   * does NOT record a draft-history entry. A no-op when no zone session is
   * active.
   */
  setZoneEditLevel: (level: ZoneEditLevel) => void;
  /**
   * setZoneSavedGeometry advances the active zone session's dirty baseline to the
   * given geometry — the shape that was just persisted — so the live draft now
   * compares equal to it and {@link selectZoneEditDirty} reads false (Save
   * disabled) until the next reshape. The commit tool calls it after a successful
   * create or update so the session stays armed but the Save button reflects "no
   * unsaved changes". A no-op when no zone session is active.
   */
  setZoneSavedGeometry: (geometry: Polygon | null) => void;
  /**
   * undoZoneDraft moves the current zone draft to `future` and restores the top
   * of `past`. A no-op when no zone session is active or nothing is behind.
   * Mirrors undoSegmentDraft.
   */
  undoZoneDraft: () => void;
}

/**
 * zoneEditInitialState seeds no active zone, no zone edit session, and no
 * in-flight create.
 */
export const zoneEditInitialState: ZoneEditSliceState = {
  activeZone: null,
  zoneCreateInheritsItinerary: false,
  zoneCreateMethod: null,
  zoneCreateOrigin: null,
  zoneEditSession: null,
  zoneOriginPicking: false,
  zonePreview: null,
};

/**
 * createZoneEditSlice builds the zone authoring session lifecycle plus the draft
 * history and metadata setters. beginZoneEdit clears the itinerary selection so
 * the itinerary-focused emphasis paint does not linger over a zone edit; every
 * draft action no-ops when no zone session is active.
 */
export const createZoneEditSlice: StateCreator<
  ZoneEditSliceState & ZoneEditSliceActions & Pick<ItinerarySliceState, "selection">,
  [],
  [],
  ZoneEditSliceActions
> = (set) => ({
  beginZoneEdit: (begin) =>
    set(() => ({
      // Clear any prior selection so the itinerary-focused emphasis paint does
      // not linger over the zone edit — the same reason beginSegmentEdit clears
      // it. The draft seeds from the optional seed geometry: an edit of a saved
      // zone opens with its shape (so the first draft change records a real
      // baseline on `past`), a create opens null (no shape until the first draw).
      selection: null,
      zoneEditSession: {
        draft: begin.seedGeometry ?? null,
        future: [],
        intent: begin.intent,
        level: begin.level,
        mode: begin.mode,
        // The create's owning org (null for an edit); the co-pilot entry and the
        // commit read it off the session so nothing needs to persist first.
        organizationId: begin.organizationId ?? null,
        past: [],
        // Seed the dirty baseline from the seed geometry: an edit opens with its
        // saved shape (so it opens clean — Save disabled until a reshape diverges),
        // a fresh create opens with null (a drawn shape is itself the change).
        savedGeometry: begin.seedGeometry ?? null,
        // Carry the computed provenance a reachability proposal opens with, so the
        // human save persists the zone WITH its source; null for a hand-drawn
        // create/edit. A later interactive reshape detaches it (see pushZoneDraft).
        source: begin.source ?? null,
        zoneId: begin.zoneId,
      },
    })),

  cancelZoneEdit: () => set({ zoneEditSession: null }),

  commitZoneEdit: () => set({ zoneEditSession: null }),

  pushZoneDraft: (geometry) =>
    set((state) => {
      const session = state.zoneEditSession;
      if (!session) {
        return {};
      }

      return {
        zoneEditSession: {
          ...session,
          draft: geometry,
          future: [],
          past: [...session.past, session.draft],
          // A human reshape of a computed draft detaches its provenance one-way:
          // the drawn shape is no longer the isochrones query's output, so the next
          // save persists the geojson, not the source. Programmatic re-seeds
          // (applyZoneDraft, undo/redo) go through setZoneEditDraft, which preserves
          // source, so only this interactive-change funnel clears it.
          source: null,
        },
      };
    }),

  redoZoneDraft: () =>
    set((state) => {
      const session = state.zoneEditSession;
      if (!session) {
        return {};
      }

      if (session.future.length === 0) {
        return {};
      }

      const [next, ...remaining] = session.future;

      return {
        zoneEditSession: {
          ...session,
          draft: next ?? null,
          future: remaining,
          past: [...session.past, session.draft],
        },
      };
    }),

  rekeyZoneEditSession: (zoneId) =>
    set((state) => {
      const session = state.zoneEditSession;
      if (!session) {
        return {};
      }

      // Swap the id and flip to an edit (reshape) session, preserving the draft,
      // the history, the mode, and the level so the drawn shape the user is
      // looking at is untouched — the create→edit promotion the first save runs.
      return { zoneEditSession: { ...session, intent: "edit", zoneId } };
    }),

  resetZoneCreate: (seed) =>
    set({
      zoneCreateInheritsItinerary: seed.inheritItinerary,
      zoneCreateMethod: seed.method,
      zoneCreateOrigin: seed.origin,
      zoneOriginPicking: false,
    }),

  setActiveZone: (activeZone) => set({ activeZone }),

  setZoneCreateMethod: (zoneCreateMethod) => set({ zoneCreateMethod }),

  // Placing the point also disarms the pick: the setter IS the one-shot, so the
  // picking hook's lifecycle tears down without an explicit stop call.
  setZoneCreateOrigin: (zoneCreateOrigin) => set({ zoneCreateOrigin, zoneOriginPicking: false }),

  setZoneEditDraft: (geometry) =>
    set((state) =>
      state.zoneEditSession
        ? { zoneEditSession: { ...state.zoneEditSession, draft: geometry } }
        : {},
    ),

  setZoneEditLevel: (level) =>
    set((state) =>
      state.zoneEditSession ? { zoneEditSession: { ...state.zoneEditSession, level } } : {},
    ),

  setZoneOriginPicking: (zoneOriginPicking) => set({ zoneOriginPicking }),

  setZonePreview: (zonePreview) => set({ zonePreview }),

  setZoneSavedGeometry: (geometry) =>
    set((state) =>
      state.zoneEditSession
        ? { zoneEditSession: { ...state.zoneEditSession, savedGeometry: geometry } }
        : {},
    ),

  undoZoneDraft: () =>
    set((state) => {
      const session = state.zoneEditSession;
      if (!session) {
        return {};
      }

      if (session.past.length === 0) {
        return {};
      }

      // The previous draft may itself be the empty null seed (the starting
      // state a create began at), so it is read with a null fallback and
      // written back as-is — an undo to the start clears the drawn shape.
      const previous = session.past[session.past.length - 1] ?? null;

      return {
        zoneEditSession: {
          ...session,
          draft: previous,
          future: [session.draft, ...session.future],
          past: session.past.slice(0, -1),
        },
      };
    }),
});
